import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Account,
  Category,
  Debt,
  Depot,
  FamilyRow,
  Goal,
  GuideMessagesResponse,
  HouseholdMe,
  ImportResponse,
  InviteVerifyResponse,
  Item,
  PayeeMapping,
  YearBundle,
  YearsResponse,
  type CategoryKind,
  type CreateAccountRequest,
  type CreateCategoryRequest,
  type CreateDebtRequest,
  type CreateDepotRequest,
  type CreateFamilyRequest,
  type CreateGoalRequest,
  type CreateItemRequest,
  type CreateYearRequest,
  type ImportPayload,
  type InviteCompleteRequest,
  type PutGeminiKeyRequest,
  type UpdateAccountRequest,
  type UpdateCategoryRequest,
  type UpdateDebtRequest,
  type UpdateDepotRequest,
  type UpdateFamilyRequest,
  type UpdateGoalRequest,
  type UpdateHouseholdRequest,
  type UpdateItemRequest,
  type UpsertPayeeMappingRequest,
} from '@budgetx/shared';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../lib/api';

// ---- Auth / household ----

export function useMe() {
  return useQuery({
    queryKey: ['me'] as const,
    queryFn: () => apiGet('/api/auth/me', HouseholdMe),
    retry: false,
  });
}

export function useUpdateHousehold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateHouseholdRequest) => apiPatch('/api/household', body, HouseholdMe),
    onSuccess: (data) => {
      if (data) qc.setQueryData(['me'], data);
    },
  });
}

export function usePutGeminiKey() {
  const qc = useQueryClient();
  const schema = z.object({ gemini_key_set: z.boolean() });
  return useMutation({
    mutationFn: (body: PutGeminiKeyRequest) => apiPut('/api/household/gemini', body, schema),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      apiPost('/api/auth/login', body, undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost('/api/auth/logout', {}, undefined),
    onSuccess: () => {
      qc.setQueryData(['me'], null);
      qc.clear();
    },
  });
}

// ---- Years ----

export function useYears() {
  return useQuery({
    queryKey: ['years'] as const,
    queryFn: () => apiGet('/api/years', YearsResponse),
  });
}

export function useYearBundle(year: number, enabled = true) {
  return useQuery({
    queryKey: ['year', year] as const,
    queryFn: () => apiGet(`/api/years/${year}`, YearBundle),
    enabled,
  });
}

export function useCreateYear() {
  const qc = useQueryClient();
  const schema = z.object({ year: z.number().int() });
  return useMutation({
    mutationFn: (body: CreateYearRequest) => apiPost('/api/years', body, schema),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['years'] }),
  });
}

export function useDeleteYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (year: number) => apiDelete(`/api/years/${year}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['years'] }),
  });
}

// ---- Categories / items ----

function invalidateYear(qc: ReturnType<typeof useQueryClient>, year: number) {
  qc.invalidateQueries({ queryKey: ['year', year] });
}

export function useCreateCategory(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCategoryRequest) =>
      apiPost(`/api/years/${year}/categories`, body, Category),
    onSuccess: () => invalidateYear(qc, year),
  });
}

export function useUpdateCategory(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number } & UpdateCategoryRequest) => {
      const { id, ...body } = input;
      return apiPatch(`/api/categories/${id}`, body, Category);
    },
    onSuccess: () => invalidateYear(qc, year),
  });
}

export function useDeleteCategory(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete(`/api/categories/${id}`),
    onSuccess: () => invalidateYear(qc, year),
  });
}

export function useCreateItem(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { categoryId: number } & CreateItemRequest) =>
      apiPost(`/api/categories/${input.categoryId}/items`, { name: input.name }, Item),
    onSuccess: () => invalidateYear(qc, year),
  });
}

/**
 * Item PATCH used for cell edits. Optimistic: apply the patch to the cached
 * year bundle immediately, roll back if the server rejects.
 */
export function useUpdateItem(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number } & UpdateItemRequest) => {
      const { id, ...body } = input;
      return apiPatch(`/api/items/${id}`, body, Item);
    },
    onMutate: async (input) => {
      const key = ['year', year] as const;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<YearBundle>(key);
      if (prev) {
        qc.setQueryData<YearBundle>(key, {
          ...prev,
          categories: prev.categories.map((c) => ({
            ...c,
            items: c.items.map((it) =>
              it.id === input.id
                ? {
                    ...it,
                    ...(input.name !== undefined ? { name: input.name } : {}),
                    ...(input.budgets !== undefined ? { budgets: input.budgets } : {}),
                    ...(input.actuals !== undefined ? { actuals: input.actuals } : {}),
                  }
                : it,
            ),
          })),
        });
      }
      return { prev };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.prev) qc.setQueryData(['year', year], ctx.prev);
    },
    onSettled: () => invalidateYear(qc, year),
  });
}

export function useDeleteItem(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete(`/api/items/${id}`),
    onSuccess: () => invalidateYear(qc, year),
  });
}

// ---- Goals ----

export function useCreateGoal(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGoalRequest) => apiPost(`/api/years/${year}/goals`, body, Goal),
    onSuccess: () => invalidateYear(qc, year),
  });
}

export function useUpdateGoal(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number } & UpdateGoalRequest) => {
      const { id, ...body } = input;
      return apiPatch(`/api/goals/${id}`, body, Goal);
    },
    onSuccess: () => invalidateYear(qc, year),
  });
}

export function useDeleteGoal(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete(`/api/goals/${id}`),
    onSuccess: () => invalidateYear(qc, year),
  });
}

// ---- Accounts ----

export function useCreateAccount(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAccountRequest) =>
      apiPost(`/api/years/${year}/accounts`, body, Account),
    onSuccess: () => invalidateYear(qc, year),
  });
}

export function useUpdateAccount(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number } & UpdateAccountRequest) => {
      const { id, ...body } = input;
      return apiPatch(`/api/accounts/${id}`, body, Account);
    },
    onSuccess: () => invalidateYear(qc, year),
  });
}

export function useDeleteAccount(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete(`/api/accounts/${id}`),
    onSuccess: () => invalidateYear(qc, year),
  });
}

// ---- Depots (year-scoped) ----

export function useCreateDepot(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDepotRequest) => apiPost(`/api/years/${year}/depots`, body, Depot),
    onSuccess: () => invalidateYear(qc, year),
  });
}

export function useUpdateDepot(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number } & UpdateDepotRequest) => {
      const { id, ...body } = input;
      return apiPatch(`/api/depots/${id}`, body, Depot);
    },
    onSuccess: () => invalidateYear(qc, year),
  });
}

export function useDeleteDepot(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete(`/api/depots/${id}`),
    onSuccess: () => invalidateYear(qc, year),
  });
}

// ---- Debts ----

export function useDebts() {
  return useQuery({
    queryKey: ['debts'] as const,
    queryFn: () => apiGet('/api/debts', z.array(Debt)),
  });
}

export function useCreateDebt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDebtRequest) => apiPost('/api/debts', body, Debt),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['debts'] }),
  });
}

export function useUpdateDebt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number } & UpdateDebtRequest) => {
      const { id, ...body } = input;
      return apiPatch(`/api/debts/${id}`, body, Debt);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['debts'] }),
  });
}

export function useDeleteDebt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete(`/api/debts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['debts'] }),
  });
}

// ---- Guide ----

export function useGuideMessages() {
  return useQuery({
    queryKey: ['guide'] as const,
    queryFn: () => apiGet('/api/guide/messages', GuideMessagesResponse),
  });
}

export function useAskGuide() {
  const qc = useQueryClient();
  const schema = z.object({ paragraphs: z.array(z.string()) });
  return useMutation({
    mutationFn: (body: { text: string; year: number; month: number }) =>
      apiPost('/api/guide/ask', body, schema),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['guide'] }),
  });
}

export function useClearGuide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete('/api/guide/messages'),
    onSuccess: () => qc.setQueryData(['guide'], { messages: [] }),
  });
}

// ---- Family admin ----

export function useFamilies() {
  return useQuery({
    queryKey: ['admin', 'families'] as const,
    queryFn: () => apiGet('/api/admin/families', z.array(FamilyRow)),
  });
}

export function useCreateFamily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFamilyRequest) => apiPost('/api/admin/families', body, FamilyRow),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'families'] }),
  });
}

export function useUpdateFamily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number } & UpdateFamilyRequest) => {
      const { id, ...body } = input;
      return apiPatch(`/api/admin/families/${id}`, body, FamilyRow);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'families'] }),
  });
}

export function useDeleteFamily() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete(`/api/admin/families/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'families'] }),
  });
}

// ---- Invite (public — no session) ----

export function useVerifyInvite() {
  return useMutation({
    mutationFn: (token: string) => apiPost('/api/invite/verify', { token }, InviteVerifyResponse),
  });
}

export function useCompleteInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteCompleteRequest) => apiPost('/api/invite/complete', body, undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

// ---- Payee mappings (bank import) ----

export function usePayeeMappings() {
  return useQuery({
    queryKey: ['payee-mappings'] as const,
    queryFn: () => apiGet('/api/payee-mappings', z.array(PayeeMapping)),
  });
}

export function useUpsertPayeeMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertPayeeMappingRequest) =>
      apiPut('/api/payee-mappings', body, PayeeMapping),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payee-mappings'] }),
  });
}

export function useDeletePayeeMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete(`/api/payee-mappings/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payee-mappings'] }),
  });
}

// ---- Import CSV ----

export function useImportYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { year: number; kind: CategoryKind; payload: ImportPayload }) =>
      apiPost(`/api/years/${input.year}/import`, input.payload, ImportResponse),
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: ['years'] });
      qc.invalidateQueries({ queryKey: ['year', input.year] });
    },
  });
}
